import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useApplyDataReview, useBulkDataChanges, useDataReviewLandings, useDiscardDataReviewJob, useFindMoreTerms, usePaintChange,
  cutoffBody, loadChange, selectInPolygon, useReviewOrder, useRevertDataReviewJob, useSetPassThreshold, useStartDataReviewJob, useStopDataReviewPass, useUndoDataChange, useUndoPaint, useUpdateDataChange,
  useWandDataChange,
} from "../../api/dataReview";
import { DrawingContext, DrawingFeatureType, DrawingMode } from "../../contexts/DrawingContext";
import { DrawPurpose, useDataReview } from "../../contexts/DataReviewContext";
import { useMap } from "../../contexts/MapContext";
import { DataChangeStatus, OrderItem, PaintRequest, TermRequest } from "../../types/dataReview";
import { bboxOfGeometry } from "../dataReview/geometry";
import { thresholdKey } from "../dataReview/visibility";
import { jobBusy, useEditorData } from "./useEditorData";
import { EditorTool, QueuedTerm, useVegEditor } from "./VegEditorContext";

const NO_ORDER: OrderItem[] = [];
const termKeyOf = (t: QueuedTerm) => t.key ?? `custom:${(t.phrase ?? "").trim().toLowerCase()}`;

/** What the editor does: start, resume and leave refinements, ask for
 *  things, decide findings one at a time or in bulk, edit outlines, paint,
 *  and apply. Every panel calls this; the state lives in the contexts. */
export function useEditorActions() {
  const navigate = useNavigate();
  const d = useEditorData();
  const e = useVegEditor();
  const { currentId, setCurrentId, selectedIds, setSelectedIds, setDrawPurpose, setTracedArea, setPassThreshold, passThresholds } = useDataReview();
  const { setDrawingMode, setDrawingFeatureType, setCoords } = useContext(DrawingContext);
  const { map } = useMap();
  const { projectId, job } = d;
  const qc = useQueryClient();
  // The active target's review order, ids only, from the server.
  const { data: orderRes } = useReviewOrder(d.job && e.phase === "session" ? projectId : undefined, job?.id, e.activeTerm, d.thr);
  const order = orderRes?.data ?? NO_ORDER;

  const startJob = useStartDataReviewJob(projectId);
  const stopPass = useStopDataReviewPass(projectId);
  const update = useUpdateDataChange(projectId);
  const bulk = useBulkDataChanges(projectId);
  const discard = useDiscardDataReviewJob(projectId);
  const revert = useRevertDataReviewJob(projectId);
  const applyMut = useApplyDataReview(projectId);
  const paintMut = usePaintChange(projectId);
  const wandMut = useWandDataChange(projectId);
  const undoMut = useUndoDataChange(projectId);
  const undoPaintMut = useUndoPaint(projectId);
  const setThresholdMut = useSetPassThreshold(projectId);

  const fail = useCallback((err: { message?: string } | null | undefined, fallback: string) => e.setError(err?.message || fallback), [e.setError]);

  // ---- drawing on the map -------------------------------------------------
  const stopDrawing = useCallback(() => {
    setCoords([]); setDrawingMode(DrawingMode.NONE); setDrawingFeatureType(DrawingFeatureType.NONE);
  }, [setCoords, setDrawingMode, setDrawingFeatureType]);
  const startDrawing = useCallback((purpose: DrawPurpose) => {
    setDrawPurpose(purpose);
    setDrawingMode(DrawingMode.DATA_AREA); setDrawingFeatureType(DrawingFeatureType.POLYGON);
  }, [setDrawPurpose, setDrawingMode, setDrawingFeatureType]);

  // ---- phases ---------------------------------------------------------------
  const goHome = useCallback(() => {
    stopDrawing(); setTracedArea(null); setCurrentId(null); setSelectedIds([]);
    e.setPhase("home"); e.setMode("review"); e.setTool("select"); e.setSheet(null); e.setBefore(false); e.setConfirmExit(false);
    e.setQueued([]); e.setActiveTerm(null); e.setRemainder(null);
  }, [stopDrawing, setTracedArea, setCurrentId, setSelectedIds, e]);

  const newRefinement = useCallback(() => {
    stopDrawing(); setCurrentId(null); setSelectedIds([]);
    e.patchSetup({ areaMode: "project", drawPoly: null, keepBuildings: true });
    e.setPhase("setup");
    e.setMode("review");
  }, [stopDrawing, setCurrentId, setSelectedIds, e]);

  const openSession = useCallback((id: number) => {
    stopDrawing(); setTracedArea(null); setCurrentId(null); setSelectedIds([]);
    e.setSessionId(id); e.setPhase("session"); e.setMode("review"); e.setTool("select");
    // Another run's target and queue mean nothing here (the queue would be sent to this run).
    e.setSheet(null); e.setBefore(false); e.setRemainder(null); e.setActiveTerm(null); e.setQueued([]);
  }, [stopDrawing, setTracedArea, setCurrentId, setSelectedIds, e]);

  const start = useCallback((imageryKey: string | undefined, area: GeoJSON.Feature | null) => {
    if (!area) { e.showToast("Draw the area on the map first."); return; }
    const name = `Refinement ${d.jobs.length + 1}`;
    startJob.mutate(
      { area, options: { start: e.setup.startMode, keep_buildings: e.setup.keepBuildings, imagery_key: imageryKey, name } },
      {
        onSuccess: (r) => {
          const id = r.data?.id;
          if (id) openSession(id);
          e.showToast(e.setup.startMode === "clean" ? "Area cleared. Find things or paint to build it back." : "Preparing the image. Ask for things straight away.");
        },
        onError: (err) => fail(err, "Could not start."),
      },
    );
  }, [e, d.jobs.length, startJob, openSession, fail]);


  // Leaving stops any outline being drawn, so it does not carry on over the project page.
  const exit = useCallback(() => {
    if (e.phase === "session" && job && job.status !== "applied") e.setConfirmExit(true);
    else { stopDrawing(); navigate(`/projects/${projectId}`); }
  }, [e, job, navigate, projectId, stopDrawing]);
  const saveExit = useCallback(() => { e.setConfirmExit(false); stopDrawing(); navigate(`/projects/${projectId}`); }, [e, navigate, projectId, stopDrawing]);
  const discardSession = useCallback(() => {
    if (!job) return goHome();
    discard.mutate(job.id, { onSuccess: () => { goHome(); e.showToast("Refinement discarded."); }, onError: (err) => fail(err, "Could not discard it.") });
  }, [job, discard, goHome, e, fail]);
  const discardJob = useCallback((jobId: number) => {
    discard.mutate(jobId, { onSuccess: () => { if (e.sessionId === jobId) goHome(); e.showToast("Refinement discarded."); }, onError: (err) => fail(err, "Could not discard it.") });
  }, [discard, e, goHome, fail]);
  const undoApplied = useCallback((jobId: number) => {
    revert.mutate(jobId, { onSuccess: () => e.showToast("Undoing that refinement…"), onError: (err) => fail(err, "Could not undo it.") });
  }, [revert, e, fail]);

  // ---- asking for things ------------------------------------------------------
  // Switching target lets go of the shape in hand: it belongs to the old
  // target, and A would otherwise accept it unseen.
  const pickTerm = useCallback((key: string) => {
    if (key !== e.activeTerm) { setCurrentId(null); setSelectedIds([]); }
    e.setActiveTerm(key);
  }, [e, setCurrentId, setSelectedIds]);
  const lookFor = useCallback((t: QueuedTerm) => {
    const key = termKeyOf(t);
    if (!t.key && !(t.phrase ?? "").trim()) return;
    if (d.groups[key] || e.queued.some((q) => termKeyOf(q) === key)) { pickTerm(key); return; }
    e.setQueued((q) => [...q, t]);
    pickTerm(key);
  }, [d.groups, e, pickTerm]);
  const stop = useCallback(() => { if (job) stopPass.mutate(job.id, { onError: (err) => fail(err, "Could not stop it.") }); }, [job, stopPass, fail]);
  const removeTerm = useCallback((term: string) => {
    // Taking back the target being looked for stops its pass.
    if (e.queued.some((x) => x.sent && termKeyOf(x) === term)) stop();
    e.setQueued((q) => q.filter((x) => termKeyOf(x) !== term));
    if (job && d.groups[term]) {
      bulk.mutate({ job_id: job.id, group: term, delete: true }, { onError: (err) => fail(err, "Could not remove it.") });
    }
    if (e.activeTerm === term) e.setActiveTerm(null);
    setCurrentId(null); setSelectedIds([]);
  }, [e, job, d.groups, bulk, fail, setCurrentId, setSelectedIds, stop]);

  // ---- walking the findings ------------------------------------------------------
  // A shape's outline: from the shapes in view, the shape in hand, or the
  // server (the target's order reaches findings far off screen).
  const geometryOf = useCallback(async (id: number) => {
    const near = d.features.find((x) => x.properties.id === id) ?? (d.current?.properties.id === id ? d.current : null);
    if (near) return near.geometry;
    if (!job) return null;
    return (await loadChange(qc, projectId, job.id, id))?.geometry ?? null;
  }, [d.features, d.current, job, qc, projectId]);
  const flyTo = useCallback(async (id: number | null) => {
    if (!map || id === null) return;
    const b = bboxOfGeometry(await geometryOf(id));
    if (!b) return;
    const centre: [number, number] = [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
    const view = map.getBounds();
    if (view && view.contains(centre)) return;
    map.easeTo({ center: centre, duration: 400 });
  }, [map, geometryOf]);
  // Zoom the map onto one shape, in the part of the map the find bar, the
  // rail and the dock leave clear, so a small finding can be found.
  const zoomTo = useCallback(async (id: number | null) => {
    if (!map || id === null) return;
    const b = bboxOfGeometry(await geometryOf(id));
    if (!b) return;
    const c = map.getContainer();
    const w = c.clientWidth, h = c.clientHeight;
    const pad = { top: Math.min(200, h * 0.25), bottom: Math.min(320, h * 0.35), left: Math.min(90, w * 0.1), right: Math.min(90, w * 0.1) };
    map.fitBounds(b, { padding: pad, maxZoom: 20, duration: 600 });
  }, [map, geometryOf]);
  const select = useCallback((id: number | null, fly = true) => {
    setSelectedIds([]); setCurrentId(id);
    if (fly) void flyTo(id);
  }, [setSelectedIds, setCurrentId, flyTo]);
  const goNext = useCallback(() => {
    const idx = order.findIndex((o) => o.id === currentId);
    const after = order.slice(idx + 1).find((o) => o.status === "pending") ?? order.find((o) => o.status === "pending" && o.id !== currentId);
    select(after?.id ?? null);
  }, [order, currentId, select]);
  const step = useCallback((dir: 1 | -1) => {
    if (!order.length) return;
    const idx = order.findIndex((o) => o.id === currentId);
    select(order[(idx + dir + order.length) % order.length].id);
  }, [order, currentId, select]);
  /** A lasso around shapes: the server picks those of the active target
   *  (or any) above the cut-off whose centre is inside. */
  const lassoSelect = useCallback(async (polygon: GeoJSON.Polygon) => {
    if (!job) return;
    try {
      const res = await selectInPolygon(projectId, job.id, polygon, e.activeTerm, cutoffBody(job.id, passThresholds));
      const ids = res.data?.ids ?? [];
      setCurrentId(null); setSelectedIds(ids);
      if (!ids.length) e.showToast("No shapes inside the lasso.");
    } catch (err) {
      fail(err as { message?: string }, "Could not select those.");
    }
  }, [job, projectId, e, passThresholds, setCurrentId, setSelectedIds, fail]);

  const decide = useCallback((status: "accepted" | "rejected") => {
    if (selectedIds.length) {
      bulk.mutate({ ids: selectedIds, status, only_pending: false }, { onError: (err) => fail(err, "Could not save that.") });
      e.showToast(`${selectedIds.length} shapes ${status}.`);
      setSelectedIds([]);
      return;
    }
    if (currentId === null) return;
    update.mutate({ id: currentId, status }, { onError: (err) => fail(err, "Could not save that.") });
    goNext();
  }, [selectedIds, bulk, e, setSelectedIds, currentId, update, goNext, fail]);
  // Target-wide actions name the group; the server groups findings the way
  // the editor does ("painted", the term, "pass:N", "other") and applies
  // the cut-off, the page's unsaved one included.
  const bulkTerm = useCallback((term: string, status: DataChangeStatus) => {
    if (!job || !d.groups[term]?.pending) return;
    setCurrentId(null); setSelectedIds([]);
    bulk.mutate({ job_id: job.id, group: term, status, thresholds: cutoffBody(job.id, passThresholds) }, { onError: (err) => fail(err, "Could not save that.") });
  }, [job, d.groups, bulk, passThresholds, setCurrentId, setSelectedIds, fail]);
  const retarget = useCallback((term: string, patch: { target?: string; asset_type?: string }) => {
    if (!job || !d.groups[term]) return;
    bulk.mutate({ job_id: job.id, group: term, include_hidden: true, ...patch }, { onError: (err) => fail(err, "Could not change that.") });
  }, [job, d.groups, bulk, fail]);
  const saveTimer = useRef<number | null>(null);
  const moveThreshold = useCallback((pass: number, v: number) => {
    if (!job) return;
    setPassThreshold(thresholdKey(job.id, pass), v);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => setThresholdMut.mutate({ jobId: job.id, pass, threshold: v }), 500);
  }, [job, setPassThreshold, setThresholdMut]);

  // ---- outlines, paint ------------------------------------------------------------
  const setTool = useCallback((t: EditorTool) => {
    stopDrawing();
    e.setTool(t);
    // Lasso select and lasso fill are freehand drags handled by the editor
    // itself; draw and cut use the map's click-to-click polygon.
    if (t === "draw" || t === "cut") startDrawing(t === "draw" ? "add" : "subtract");
  }, [stopDrawing, e, startDrawing]);
  const beginEdit = useCallback(() => {
    if (currentId === null) return;
    setSelectedIds([]);
    e.setMode("edit"); setTool("wand");
  }, [currentId, setSelectedIds, e, setTool]);
  const endEdit = useCallback((accept: boolean) => {
    if (accept && currentId !== null) update.mutate({ id: currentId, status: "accepted" }, { onError: (err) => fail(err, "Could not save that.") });
    e.setMode("review"); setTool("select");
  }, [currentId, update, e, setTool, fail]);
  const editOutline = useCallback((op: "add" | "subtract", geometry: GeoJSON.Polygon, onSettled?: (ok: boolean) => void) => {
    if (currentId === null) { onSettled?.(false); return; }
    update.mutate({ id: currentId, edit: { op, geometry } }, {
      onSuccess: (r) => { const n = r.data?.properties?.merged?.length ?? 0; if (n) e.showToast(`Merged with ${n} other shape${n === 1 ? "" : "s"}.`); onSettled?.(true); },
      onError: (err) => { fail(err, "Could not change the outline."); onSettled?.(false); },
    });
  }, [currentId, update, e, fail]);
  const undoOutline = useCallback(() => {
    if (currentId === null) return;
    undoMut.mutate(currentId, { onError: (err) => e.showToast(err?.message || "Nothing to undo.") });
  }, [currentId, undoMut, e]);
  const toPaint = useCallback(() => {
    setCurrentId(null); setSelectedIds([]);
    e.setPaintMark(d.paintOps);
    e.setMode("paint"); setTool("brush");
  }, [setCurrentId, setSelectedIds, e, setTool, d.paintOps]);
  const toReview = useCallback(() => { e.setMode("review"); setTool("select"); }, [e, setTool]);
  const undoPaint = useCallback(() => {
    if (!job || d.paintOps <= e.paintMark) return;
    undoPaintMut.mutate({ jobId: job.id }, { onError: (err) => fail(err, "Could not undo that.") });
  }, [job, d.paintOps, e.paintMark, undoPaintMut, fail]);
  // Cancel: every stroke since paint mode was entered is taken back, then
  // back to the refinement.
  const cancelPaint = useCallback(() => {
    if (job && d.paintOps > e.paintMark) {
      undoPaintMut.mutate({ jobId: job.id, to: e.paintMark }, { onError: (err) => fail(err, "Could not take the painting back.") });
    }
    e.setMode("review"); setTool("select");
  }, [job, d.paintOps, e, undoPaintMut, fail, setTool]);
  // mutateAsync, not mutate: with several strokes in flight, mutate only
  // calls back for the latest, and earlier strokes never learn they landed.
  const paint = useCallback((req: PaintRequest, onSettled?: (ok: boolean) => void) => {
    if (!job) { onSettled?.(false); return; }
    paintMut.mutateAsync({ jobId: job.id, ...req }).then(
      () => onSettled?.(true),
      (err) => { fail(err, "Could not paint there."); onSettled?.(false); },
    );
  }, [job, paintMut, fail]);
  const wandAt = useCallback((lng: number, lat: number) => {
    if (!job) return;
    if (e.mode === "paint") {
      paint({ wand: { lng, lat, tolerance: e.tolerance }, target: e.paintClass });
      return;
    }
    if (e.mode === "edit" && currentId !== null) {
      wandMut.mutate(
        { id: currentId, lng, lat, tolerance: e.tolerance, op: e.tool === "unwand" ? "subtract" : "add" },
        { onError: (err) => e.showToast(err?.message || "Nothing picked up there.") },
      );
    }
  }, [job, e, paint, currentId, wandMut]);
  const fillRest = useCallback(() => {
    e.setRemainder({ mode: "class", veg_class: e.paintClass });
    e.showToast(`What's left becomes ${e.paintClass} when you apply.`, () => e.setRemainder(null));
  }, [e]);

  // ---- applying --------------------------------------------------------------------
  const apply = useCallback(() => {
    if (!job) return;
    applyMut.mutate({ job_id: job.id, remainder: e.remainder }, {
      onSuccess: () => { e.setSheet(null); e.setBefore(false); e.showToast("Applying…"); },
      onError: (err) => fail(err, "Could not apply."),
    });
  }, [job, applyMut, e, fail]);
  // One object per change, not per render: the editor's key and map
  // listeners depend on it and would otherwise re-register every render.
  const busyStart = startJob.isPending, busyApply = applyMut.isPending, busyUndoPaint = undoPaintMut.isPending;
  return useMemo(() => ({
    stopDrawing, startDrawing, goHome, newRefinement, openSession, start, exit, saveExit, discardSession, discardJob, undoApplied,
    pickTerm, lookFor, stop, removeTerm, select, zoomTo, goNext, step, decide, lassoSelect, order, bulkTerm, retarget, moveThreshold,
    setTool, beginEdit, endEdit, editOutline, undoOutline, toPaint, toReview, undoPaint, cancelPaint, paint, wandAt, fillRest, apply, flyTo,
    busy: { start: busyStart, apply: busyApply, undoPaint: busyUndoPaint },
    passThresholds,
  }), [
    stopDrawing, startDrawing, goHome, newRefinement, openSession, start, exit, saveExit, discardSession, discardJob, undoApplied,
    pickTerm, lookFor, stop, removeTerm, select, zoomTo, goNext, step, decide, lassoSelect, order, bulkTerm, retarget, moveThreshold,
    setTool, beginEdit, endEdit, editOutline, undoOutline, toPaint, toReview, undoPaint, cancelPaint, paint, wandAt, fillRest, apply, flyTo,
    busyStart, busyApply, busyUndoPaint, passThresholds,
  ]);
}

/** The editor's background work, mounted once (by the editor itself, not
 *  by every panel that uses the actions): draining the queue of things to
 *  look for, noticing an apply landing, and refetching findings when a run
 *  changes state. */
export function useEditorEffects() {
  const qc = useQueryClient();
  const d = useEditorData();
  const e = useVegEditor();
  const a = useEditorActions();
  const findMore = useFindMoreTerms(d.projectId);
  const { job, projectId } = d;

  // The queue drains one target at a time, as soon as the run can take it.
  // The item is marked sent the moment the request goes, so its pill turns
  // to a spinner at once and stays one through the hand-off: the cached run
  // still reads "review" until the summary refetches, and the first stages
  // ("Starting", "Reading the photo") name no target.
  const draining = useRef(false);
  useEffect(() => {
    if (!job || job.status !== "review" || !e.queued.length || e.queued.some((q) => q.sent) || draining.current || findMore.isPending) return;
    const next = e.queued[0];
    const key = termKeyOf(next);
    const term: TermRequest = next.key ? { key: next.key, target: next.target, asset_type: next.asset_type } : { phrase: next.phrase!, target: next.target, asset_type: next.asset_type };
    draining.current = true;
    const sentPass = Number(job.stats?.pass ?? 0);
    e.setQueued((q) => q.map((x) => (termKeyOf(x) === key ? { ...x, sent: true, sentPass } : x)));
    findMore.mutate({ jobId: job.id, terms: [term] }, {
      onSettled: () => { draining.current = false; },
      onError: (err) => { e.setQueued((q) => q.filter((x) => termKeyOf(x) !== key)); e.setError(err?.message || "Could not start looking."); },
    });
  }, [job?.id, job?.status, e.queued, findMore.isPending]);

  // The sent item's life: seen busy, then seen back in review (or its pass
  // number moved on), then gone once findings fetched after that arrive.
  // A failed run lets it go at once.
  useEffect(() => {
    const sent = e.queued.find((q) => q.sent);
    if (!sent || !job) return;
    const key = termKeyOf(sent);
    const patch = (p: Partial<QueuedTerm>) => e.setQueued((q) => q.map((x) => (x.sent && termKeyOf(x) === key ? { ...x, ...p } : x)));
    const drop = () => e.setQueued((q) => q.filter((x) => !(x.sent && termKeyOf(x) === key)));
    if (job.status === "failed") { drop(); return; }
    const busy = jobBusy(job);
    if (busy) { if (!sent.seenBusy) patch({ seenBusy: true }); return; }
    const moved = Number(job.stats?.pass ?? 0) > (sent.sentPass ?? 0);
    if (!sent.settledAt) { if (sent.seenBusy || moved) patch({ settledAt: Date.now() }); return; }
    if (d.groupsUpdatedAt > sent.settledAt || Date.now() - sent.settledAt > 15_000) drop();
  }, [job?.status, job?.stats?.pass, e.queued, d.groupsUpdatedAt]);

  // When an apply or an undo lands: a toast (with Undo after an apply), and
  // the refinement that was applied closes. The project page refreshes the
  // layers and the strategy (it does so after Save & exit too).
  useDataReviewLandings(projectId, (landed) => {
    for (const { job: j, kind } of landed) {
      if (kind === "applied") {
        if (e.sessionId === j.id) { a.goHome(); e.setSessionId(null); }
        e.showToast("Applied. Fuel loads are recalculating.", () => a.undoApplied(j.id));
      } else if (kind === "reverted") {
        e.showToast("Undone. Fuel loads are recalculating.");
      } else {
        e.setError(j.error || (kind === "apply-failed" ? "Could not apply." : "Could not undo it."));
      }
    }
  });

  // A run finishing (or changing status) means the findings on the map are stale.
  const signature = (d.summary?.jobs ?? []).map((j) => `${j.id}:${j.status}`).join(",");
  const lastSignature = useRef(signature);
  useEffect(() => {
    if (lastSignature.current !== signature) {
      lastSignature.current = signature;
      for (const part of ["data-review-changes", "data-review-groups", "data-review-order"]) {
        qc.invalidateQueries({ queryKey: ["project", projectId, part] });
      }
    }
  }, [signature]);
}
